import {
  PersistentTableColumn,
  useGetPersistentTableSettingsQuery,
  useUpsertPersistentTableSettingsMutation,
} from "~/store/mystore/persistentTableSettings.redux";
import { useCallback, useEffect, useMemo, useState } from "react";

import { Column } from "~/components/adTable/columnSelect";
import { PERSISTENT_TABLE_IDS } from "~/utils/persistentTableIdsEnum";
import debounce from "lodash/debounce";

interface UsepersistentColumnsListParams {
  tableId: PERSISTENT_TABLE_IDS;
  initialColumns: Column[];
  // are we currently in a report as opposed to a live view?
  report: boolean;
}

/**
 * A hook to manage persistent column settings for tables
 *
 * This hook handles:
 * - Loading saved column preferences from the backend
 * - Saving column visibility changes to the backend
 * - Managing local column state
 *
 */
export function usePersistentTableColumns({
  tableId,
  initialColumns,
  report,
}: UsepersistentColumnsListParams): {
  myColumns: Column[];
  setMyColumns: (columns: Column[]) => void;
} {
  const [columnSelection, setColumnSelection] = useState<
    PersistentTableColumn[]
  >([]);

  // Mutation to update columns
  const [upsertColumns] = useUpsertPersistentTableSettingsMutation();

  // Fetch persistent columns from the mongo collection with caching disabled
  const { persistentColumnsList, isFetching } =
    useGetPersistentTableSettingsQuery(
      {
        tableId,
      },
      {
        skip: report,
        selectFromResult: ({ data, isFetching }) => ({
          persistentColumnsList: data?.data?.columns ?? [],
          isFetching,
        }),
        refetchOnMountOrArgChange: true,
      }
    );

  useEffect(() => {
    if (!isFetching && persistentColumnsList.length > 0) {
      if (
        JSON.stringify(persistentColumnsList) !==
        JSON.stringify(columnSelection)
      ) {
        setColumnSelection(persistentColumnsList);
      }
    }
  }, [isFetching, persistentColumnsList]);

  // Debounced function to update persistent storage when the columns change in the UI
  const updatePersistentColumnsList = useCallback(
    debounce(async (columnsToPersist: PersistentTableColumn[]) => {
      try {
        await upsertColumns({
          tableId,
          columns: columnsToPersist,
        }).unwrap();
      } catch (e) {
        console.error("Failed to persist columns:", e);
      }
    }, 1000),
    [upsertColumns, tableId]
  );

  const externalSetMyColumns = (columns: Column[]) => {
    const columnsToPersist = columns.map(({ id, isVisible }) => ({
      id: id ?? "",
      isVisible: isVisible ?? false,
    }));
    setColumnSelection(columnsToPersist);
    updatePersistentColumnsList(columnsToPersist);
  };

  const updatedColumns = useMemo(() => {
    if (report) {
      return initialColumns;
    }

    return initialColumns.map((column) => {
      const persistentColumn = columnSelection.find(
        (pCol) => pCol.id === column.id
      );
      return {
        ...column,
        isVisible: persistentColumn
          ? persistentColumn.isVisible
          : column.isVisible,
      };
    });
  }, [initialColumns, columnSelection, report]);

  return {
    myColumns: updatedColumns,
    setMyColumns: externalSetMyColumns,
  };
}
